import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Mail, Lock, Eye, EyeOff, ArrowLeft, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      // Normalize email to lowercase before sending
      const response = await axios.post(`${API}/admin/login`, {
        ...formData,
        email: formData.email.toLowerCase().trim()
      });
      const { token, user, message } = response.data;
      
      localStorage.setItem("expertbase_admin_token", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      
      if (message) {
        toast.success(message);
      } else {
        toast.success("Welcome back, Admin!");
      }
      
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex flex-col">
      {/* Header with Back Button */}
      <header className="bg-charcoal/50 border-b border-slate-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <span className="font-display text-xl text-gold-gradient">ExpertBase</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-gold-500" />
            </div>
            <h1 className="font-display text-3xl text-gold-gradient">ExpertBase</h1>
            <p className="text-slate-500 mt-2">Admin Portal</p>
          </div>

        <div className="bg-charcoal border border-slate-800 p-8 rounded-sm">
          <h2 className="font-display text-2xl mb-6 text-center">Admin Login</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@expertbase.ai"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="pl-12 bg-obsidian border-slate-700 focus:border-gold-500 h-12"
                  data-testid="admin-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="pl-12 pr-12 bg-obsidian border-slate-700 focus:border-gold-500 h-12"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="text-center mt-6 space-y-3">
            <Link 
              to="/admin/forgot-password" 
              className="text-gold-500 hover:text-gold-400 text-sm flex items-center justify-center gap-2"
              data-testid="admin-forgot-password-link"
            >
              <KeyRound className="w-4 h-4" />
              Forgot Password?
            </Link>
            <p className="text-slate-600 text-sm">
              First time? Use setup password to create admin account.
            </p>
          </div>
        </div>
      </motion.div>
      </div>
    </div>
  );
}
